import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Paper, Card } from '@mui/material';

// Layout Components
export const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

export const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

export const FlexBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
}));

// Typography Components
export const PageTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h4,
  marginBottom: theme.spacing(3),
  color: theme.palette.text.primary,
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h5,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
}));

export const SubsectionTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
}));

// Card Components
export const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  transition: theme.transitions.create(['box-shadow', 'transform'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    boxShadow: theme.shadows[2],
    transform: 'translateY(-2px)',
  },
}));

export const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
}));

// Button Components
export const ActionButton = styled(Button)(({ theme, variant = 'contained' }) => ({
  height: '36px',
  minWidth: '100px',
  borderRadius: '100px',
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  textTransform: 'none',
  transition: 'all 0.2s ease',
  
  ...(variant === 'contained' && {
    backgroundColor: '#4F6BFF',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#3D55DB',
    },
  }),
  
  ...(variant === 'outlined' && {
    border: '1.5px solid #4F6BFF',
    color: '#4F6BFF',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(79, 107, 255, 0.08)',
      borderColor: '#3D55DB',
    },
  }),
  
  ...(variant === 'view' && {
    border: '1.5px solid #4F6BFF',
    color: '#4F6BFF',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(79, 107, 255, 0.08)',
      borderColor: '#3D55DB',
    },
  }),
  
  ...(variant === 'danger' && {
    border: '1.5px solid #FF3B30',
    color: '#FF3B30',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(255, 59, 48, 0.08)',
      borderColor: '#FF3B30',
    },
  }),
}));

// Specific button variants
export const AddButton = styled(Button)(({ theme }) => ({
  height: '30px',
  minWidth: '100px',
  borderRadius: '100px',
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: '#4F6BFF',
  color: '#FFFFFF',
  '&:hover': {
    backgroundColor: '#3D55DB',
  },
}));

export const MessageButton = styled(Button)(({ theme }) => ({
  height: '30px',
  minWidth: '75px',
  borderRadius: '75px',
  padding: '8px 16px',
  fontSize: theme.typography.button.fontSize,
  fontWeight: 500,
  textTransform: 'none',
  border: '1.5px solid #4F6BFF',
  color: '#4F6BFF',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(79, 107, 255, 0.08)',
    borderColor: '#3D55DB',
  },
}));

export const ViewButton = styled(Button)(({ theme }) => ({
  height: '30px',
  minWidth: '75px',
  borderRadius: '75px',
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  textTransform: 'none',
  border: '1.5px solid #4F6BFF',
  color: '#4F6BFF',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(79, 107, 255, 0.08)',
    borderColor: '#3D55DB',
  },
}));

export const RemoveButton = styled(Button)(({ theme }) => ({
  height: '30px',
  minWidth: '75px',
  borderRadius: '75px',
  padding: '8px 16px',
  fontSize: theme.typography.button.fontSize,
  fontWeight: 500,
  textTransform: 'none',
  border: '1.5px solid #FF3B30',
  color: '#FF3B30',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255, 59, 48, 0.08)',
    borderColor: '#FF3B30',
  },
}));


export const ManageButton = styled(Button)(({ theme }) => ({
  height: '30px',
  minWidth: '75px',
  borderRadius: '75px',
  padding: '8px 16px',
  fontSize: theme.typography.button.fontSize,
  fontWeight: 500,
  textTransform: 'none',
  border: '1.5px solid #4F6BFF',
  color: '#4F6BFF',
  backgroundColor: 'transparent',
  '&:hover': {
  backgroundColor: 'rgba(79, 107, 255, 0.08)',
  borderColor: '#3D55DB',
  },
}));

// Loading button with loading state
export const LoadingButton = styled(Button)(({ theme, loading }) => ({
  height: '48px',
  minWidth: '120px',
  borderRadius: '100px',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  position: 'relative',
  backgroundColor: '#4F6BFF',
  color: '#FFFFFF',
  '&:hover': {
    backgroundColor: '#3D55DB',
  },
  '&.loading': {
    color: 'transparent',
    pointerEvents: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: '20px',
      height: '20px',
      marginTop: '-10px',
      marginLeft: '-10px',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      borderTopColor: '#fff',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    },
  },
  '@keyframes spin': {
    to: { transform: 'rotate(360deg)' },
  },
}));

// List Components
export const ListItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

// Form Components
export const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const FormRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

// Status Components
export const StatusBadge = styled(Box)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'error':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.shape.borderRadiusSmall,
    backgroundColor: `${getStatusColor()}20`,
    color: getStatusColor(),
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 500,
  };
});

// Info Box Components
export const InfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: '20px',
  display: 'flex',
  alignItems: 'center',
  border: '1px solid',
  borderColor: theme.palette.primary.light,
  backgroundColor: theme.palette.background.paper,
  minWidth: { xs: '100%', sm: 'auto' },
  flex: { xs: '1 1 100%', sm: '1 1 auto' },
  transition: 'all 0.3s ease',
  '& .MuiTypography-root': {
    fontSize: '0.75rem', // Smaller font size
    lineHeight: 1.2, // Tighter line height
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1rem', // Smaller icon size
  },
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    '& .MuiSvgIcon-root, & .MuiTypography-root': {
      color: 'white',
    },
  },
}));